import type { EntraBrowserConfig, EntraConfigStatus } from './entraConfig';
import { entraAuthority } from './entraConfig';

// ─────────────────────────────────────────────────────────────────────
// STAFF AUTH — internal Starpoint staff sign-in, behind one boundary.
//
// Entra proves WHO someone is. It decides nothing else. Everything this module
// hands back is identity or a token; there is no role, no tenant and no
// permission anywhere in its types, because the browser is not entitled to an
// opinion about any of them. The API re-derives the identity from the token it
// verifies itself (apps/api/src/auth/entra.ts) and looks up authority in
// Starpoint's own tables. A client that "knew" it was an admin would be
// telling itself a story.
//
// Two things are deliberately NOT here:
//
//   • token storage. MSAL owns its cache (sessionStorage by default) and
//     handles renewal. Copying an access token into app state, localStorage or
//     a module variable would create a second copy with no expiry handling and
//     no way to revoke it. `getApiToken()` therefore asks MSAL every time and
//     returns the value to the caller without keeping it.
//   • token logging. Nothing in this file passes token material to `console`.
//     That is not fussiness: lib/consoleCapture.ts records console.error/warn
//     into a ring buffer that the bug-report flow attaches to an OUTBOUND
//     e-mail. A token logged here would leave the building.
// ─────────────────────────────────────────────────────────────────────

/**
 * Staff identity, for display only.
 *
 * `subject` is the Entra `oid` — the same immutable id the API uses as its
 * mapping key — and is exposed so an operator can confirm which directory
 * object signed in. It is not a credential and it is not authority: the server
 * reads the subject from the VERIFIED token, never from anything the browser
 * says about itself.
 */
export interface StaffIdentity {
  subject: string | null;
  email: string | null;
  displayName: string | null;
}

export type StaffAuthState =
  /** No Entra configuration — the feature is off. */
  | { status: 'disabled' }
  /** Configured, but wrongly. Named problems, never a silent fallback. */
  | { status: 'misconfigured'; missing: string[]; problems: string[] }
  | { status: 'signed-out' }
  | { status: 'signing-in' }
  | { status: 'signed-in'; identity: StaffIdentity }
  /** A reason code plus a message already safe to render. Never a token. */
  | { status: 'error'; reason: StaffAuthErrorReason; message: string };

export type StaffAuthErrorReason =
  | 'popup_blocked'
  | 'cancelled'
  | 'interaction_required'
  /** MSAL believes a sign-in is already running. See clearStaleInteraction. */
  | 'interaction_in_progress'
  | 'not_signed_in'
  | 'unavailable';

/*
 * ON «INTERACTION IN PROGRESS», AND WHY NOTHING IS CLEARED HERE.
 *
 * MSAL sets a flag when an interactive flow starts and clears it when the flow
 * COMPLETES. A flow that HANGS leaves it set, and every later attempt is then
 * refused before a popup is even opened.
 *
 * That is how it was found: the v5 bridge was missing (#380), sign-in hung, and
 * the next attempt reported «Kunne ikke nå Microsoft-innlogging» — blaming a
 * server that was never contacted.
 *
 * The obvious repair is to delete MSAL's key from sessionStorage. This
 * deliberately does NOT, for two reasons.
 *
 * FIRST, 5.21 offers no supported way to do it. `BrowserCacheManager` has an
 * `allowOverride` path that cancels the pending monitor and clears the key,
 * but nothing outside that class passes it and no public request option
 * reaches it. The only public lever is `clearCache()`, which drops the token
 * cache — «sign out», not «recover». Deleting the key by hand is reaching into
 * another library's internals on the strength of a name, and names change
 * inside a major version.
 *
 * SECOND, and better: the stale flag was a SYMPTOM. The broken bridge is what
 * left flows unfinished. With the redirect bridge in place they complete, MSAL
 * clears its own flag, and there is nothing to repair.
 *
 * So the condition is reported accurately and the person is told what to do.
 * If it turns out to recur with a working bridge, that is evidence worth
 * having before reaching into someone else's cache.
 */

// ── The slice of MSAL this module uses ──────────────────────────────
// Declared structurally so the tests drive a fake and CI never contacts
// Microsoft. The shapes match @azure/msal-browser; the narrowness is the
// point — anything outside this list is not reachable from app code.

export interface MsalAccountLike {
  homeAccountId: string;
  localAccountId?: string;
  username?: string;
  name?: string;
  idTokenClaims?: Record<string, unknown>;
}

export interface MsalTokenResultLike {
  accessToken: string;
  account?: MsalAccountLike | null;
}

export interface MsalClientLike {
  initialize(): Promise<void>;
  getAllAccounts(): MsalAccountLike[];
  getActiveAccount(): MsalAccountLike | null;
  setActiveAccount(account: MsalAccountLike | null): void;
  loginPopup(request: { scopes: string[] }): Promise<MsalTokenResultLike>;
  /**
   * The SAME sign-in as a full-page navigation (#789). Same client, same
   * redirect URI, same state/nonce handling — only the window differs.
   */
  loginRedirect(request: { scopes: string[] }): Promise<void>;
  /**
   * Process a response a redirect sign-in brought back, or resolve null when
   * this page load is not a return from one. MSAL's own callback handling:
   * `auth-callback.ts` caches the response and navigates back here.
   */
  handleRedirectPromise(): Promise<MsalTokenResultLike | null>;
  acquireTokenSilent(request: {
    scopes: string[];
    account: MsalAccountLike;
  }): Promise<MsalTokenResultLike>;
  acquireTokenPopup(request: { scopes: string[] }): Promise<MsalTokenResultLike>;
  logoutPopup(request?: { account?: MsalAccountLike | null }): Promise<void>;
}

export type MsalFactory = (config: EntraBrowserConfig) => MsalClientLike | Promise<MsalClientLike>;

/**
 * Map an MSAL account to a Starpoint staff identity.
 *
 * Exported because this is the one place a claim could leak into the app, and
 * it should be readable and testable on its own. Note what is NOT copied: an
 * Entra token can carry `roles`, `groups` and `wids`, and a directory admin
 * can add any of them. None becomes Starpoint authority — not here, and not on
 * the server, which discards them at the boundary too.
 */
export function staffIdentityFrom(account: MsalAccountLike | null): StaffIdentity | null {
  if (!account) return null;
  const claims = account.idTokenClaims ?? {};
  const oid = typeof claims.oid === 'string' && claims.oid ? claims.oid : null;
  return {
    subject: oid ?? account.localAccountId ?? null,
    email: account.username ?? null,
    displayName: account.name ?? null,
  };
}

/** Classify a failure into a reason code and Norwegian operator-facing text. */
export function describeAuthFailure(error: unknown): {
  reason: StaffAuthErrorReason;
  message: string;
} {
  const code =
    typeof error === 'object' && error !== null && 'errorCode' in error
      ? String((error as { errorCode: unknown }).errorCode)
      : '';
  const name = error instanceof Error ? error.name : '';

  // `access_denied` is what Entra REDIRECTS back with when the person cancels
  // at Microsoft, or declines consent (#789). Under the popup flow the same
  // act surfaces as `user_cancelled`; it is the same event, and reporting it
  // as «could not reach Microsoft» would be untrue.
  if (
    code === 'user_cancelled' ||
    code === 'access_denied' ||
    name === 'BrowserAuthError:user_cancelled'
  ) {
    return { reason: 'cancelled', message: 'Innloggingen ble avbrutt.' };
  }
  // SAY WHAT ACTUALLY HAPPENED. This used to fall through to «could not reach
  // Microsoft», which is false — Microsoft was never contacted. The stale flag
  // is cleared by the caller, so the retry this suggests can succeed.
  if (code.includes('interaction_in_progress')) {
    return {
      reason: 'interaction_in_progress',
      // Actionable, because the recovery is the person's to perform: MSAL's
      // flag lives in sessionStorage, which a new tab does not inherit.
      message:
        'En innlogging er allerede i gang. Lukk eventuelle innloggingsvinduer, ' +
        'eller åpne siden i en ny fane, og prøv igjen.',
    };
  }
  if (code.includes('popup_window_error') || code.includes('popup_blocked')) {
    return {
      reason: 'popup_blocked',
      message: 'Nettleseren blokkerte innloggingsvinduet. Tillat popup for dette nettstedet.',
    };
  }
  if (name === 'InteractionRequiredAuthError' || code === 'interaction_required') {
    return { reason: 'interaction_required', message: 'Logg inn på nytt for å fortsette.' };
  }
  return {
    reason: 'unavailable',
    message: 'Kunne ikke nå Microsoft-innlogging. Prøv igjen.',
  };
}

export interface StaffAuth {
  /** Current state. Synchronous, so a render never has to await. */
  getState(): StaffAuthState;
  /** Whether the Entra flow is usable at all. */
  readonly available: boolean;
  signIn(): Promise<StaffAuthState>;
  /**
   * Sign in by navigating the whole page to Microsoft (#789).
   *
   * For a start nobody clicked: a browser blocks a popup that no user gesture
   * opened, so `/sa` sending a signed-out visitor straight to Entra has to be
   * a navigation. The response comes back through the same callback page and
   * is processed by `restore()` on the page it returns to. Resolves
   * `signing-in` while the page is leaving, or `error` if it could not start.
   */
  signInRedirect(): Promise<StaffAuthState>;
  signOut(): Promise<StaffAuthState>;
  /**
   * An access token for the STARPOINT API audience — not an ID token.
   * Silent first, interactive only when MSAL says interaction is required.
   * Returns null when nobody is signed in; the caller decides what that means.
   */
  getApiToken(): Promise<string | null>;
  /**
   * Re-read MSAL's cache (an existing session survives a page reload), first
   * processing a redirect response if this page load is a return from one.
   * Concurrent calls share one answer.
   */
  restore(): Promise<StaffAuthState>;
  subscribe(listener: (state: StaffAuthState) => void): () => void;
}

/**
 * Build the auth boundary. `factory` is injected so tests drive a fake MSAL;
 * in the app it is `createMsalClient` from ./msalClient.
 */
export function createStaffAuth(status: EntraConfigStatus, factory: MsalFactory): StaffAuth {
  let state: StaffAuthState =
    status.state === 'configured'
      ? { status: 'signed-out' }
      : status.state === 'disabled'
        ? { status: 'disabled' }
        : { status: 'misconfigured', missing: status.missing, problems: status.problems };

  const listeners = new Set<(s: StaffAuthState) => void>();
  const set = (next: StaffAuthState) => {
    state = next;
    for (const l of listeners) l(next);
    return next;
  };

  const available = status.state === 'configured';
  const config = status.state === 'configured' ? status.config : null;

  // One client, created on first use. Building it eagerly would run MSAL's
  // initialisation on every page load including the ones that never sign in.
  let clientPromise: Promise<MsalClientLike> | null = null;
  const client = async (): Promise<MsalClientLike> => {
    if (!config) throw new Error('Entra is not configured');
    if (!clientPromise) {
      clientPromise = (async () => {
        const c = await factory(config);
        await c.initialize();
        return c;
      })().catch((e) => {
        clientPromise = null; // a failed init must not poison every later attempt
        throw e;
      });
    }
    return clientPromise;
  };

  const scopes = () => (config ? [config.apiScope] : []);

  const activeAccount = (c: MsalClientLike): MsalAccountLike | null =>
    c.getActiveAccount() ?? c.getAllAccounts()[0] ?? null;

  // ── A REDIRECT RESPONSE IS PROCESSED ONCE PER PAGE LOAD (#789) ────
  //
  // MSAL reads the response the callback page cached and then deletes it, so
  // a second read answers null — and two callers racing on mount (the hook and
  // `/sa`'s own entry decision) must not get two different answers about the
  // same return. Memoised on this instance, which lives as long as the page.
  let redirectOutcome: Promise<MsalTokenResultLike | null> | null = null;
  const processRedirect = (c: MsalClientLike) => (redirectOutcome ??= c.handleRedirectPromise());

  // Concurrent restores share one promise, for the same reason.
  let restoring: Promise<StaffAuthState> | null = null;

  return {
    available,
    getState: () => state,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    restore() {
      if (!available) return Promise.resolve(state);
      restoring ??= (async (): Promise<StaffAuthState> => {
        try {
          const c = await client();
          // A FAILED RETURN IS AN ERROR STATE, not «signed out»: Entra said
          // no, and a caller that read it as «nobody has tried» would send
          // the person straight back — the loop #789 must not have.
          const returned = await processRedirect(c);
          if (returned?.account) c.setActiveAccount(returned.account);
          const account = activeAccount(c);
          if (!account) return set({ status: 'signed-out' });
          c.setActiveAccount(account);
          const identity = staffIdentityFrom(account);
          return identity ? set({ status: 'signed-in', identity }) : set({ status: 'signed-out' });
        } catch (e) {
          return set({ status: 'error', ...describeAuthFailure(e) });
        }
      })().finally(() => {
        restoring = null;
      });
      return restoring;
    },

    async signInRedirect() {
      if (!available) return state;
      set({ status: 'signing-in' });
      try {
        const c = await client();
        // MSAL refuses a new redirect while a returned one is unprocessed, so
        // settle that first. Its OUTCOME is not this call's business — a
        // failed earlier return is exactly what a retry is for.
        await processRedirect(c).catch(() => null);
        await c.loginRedirect({ scopes: scopes() });
        // The page is navigating away. Nothing is set: `signing-in` is the
        // truth until it has gone, and the return is `restore()`'s to read.
        return state;
      } catch (e) {
        return set({ status: 'error', ...describeAuthFailure(e) });
      }
    },

    async signIn() {
      if (!available) return state;
      set({ status: 'signing-in' });
      try {
        const c = await client();
        // Asking for the API scope at sign-in means consent is collected once,
        // and the very first acquireTokenSilent can be served from the cache.
        const result = await c.loginPopup({ scopes: scopes() });
        const account = result.account ?? activeAccount(c);
        if (!account) {
          return set({
            status: 'error',
            reason: 'unavailable',
            message: 'Microsoft returnerte ingen konto.',
          });
        }
        c.setActiveAccount(account);
        const identity = staffIdentityFrom(account);
        return identity
          ? set({ status: 'signed-in', identity })
          : set({
              status: 'error',
              reason: 'unavailable',
              message: 'Microsoft returnerte ingen konto.',
            });
      } catch (e) {
        return set({ status: 'error', ...describeAuthFailure(e) });
      }
    },

    async signOut() {
      if (!available) return state;
      try {
        const c = await client();
        const account = activeAccount(c);
        await c.logoutPopup({ account });
        c.setActiveAccount(null);
      } catch {
        // Signing out must always end signed out locally. A failed round-trip
        // to Microsoft leaves their session alone, which is theirs to end.
      }
      return set({ status: 'signed-out' });
    },

    async getApiToken() {
      if (!available) return null;
      let c: MsalClientLike;
      try {
        c = await client();
      } catch {
        return null;
      }
      const account = activeAccount(c);
      if (!account) return null;
      try {
        const silent = await c.acquireTokenSilent({ scopes: scopes(), account });
        return silent.accessToken || null;
      } catch (e) {
        // Silent renewal fails when the refresh token has expired or consent
        // was revoked. That is the ONLY case worth a popup; anything else is
        // an error the caller should see as "no token".
        const { reason } = describeAuthFailure(e);
        if (reason !== 'interaction_required') return null;
        try {
          const interactive = await c.acquireTokenPopup({ scopes: scopes() });
          return interactive.accessToken || null;
        } catch (inner) {
          set({ status: 'error', ...describeAuthFailure(inner) });
          return null;
        }
      }
    },
  };
}

/** The real MSAL client. Separated so the module above imports no MSAL types. */
export async function createMsalClient(config: EntraBrowserConfig): Promise<MsalClientLike> {
  const { PublicClientApplication } = await import('@azure/msal-browser');
  return new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      // Tenant-pinned. `/common` would accept any Microsoft directory.
      authority: entraAuthority(config.tenantId),
      redirectUri: config.redirectUri,
    },
    cache: {
      // MSAL's default, stated explicitly because it is a security decision:
      // sessionStorage is scoped to the tab and cleared when it closes.
      // localStorage would leave tokens on a shared club machine.
      cacheLocation: 'sessionStorage',
    },
  }) as unknown as MsalClientLike;
}
